import { RefObject, useEffect, useMemo } from 'react'

import { events } from 'analytics/events'
import { track } from 'utils/analytics'

import {
  DetailsCollapsible,
  trackDetailsSectionOpened,
} from 'components/common/details/shared'

import {
  getMealRatingEventProps,
  MealRating,
  MealReviewSummary,
  MealReviewSummaryState,
  useMealReviewSummary,
} from './hooks'
import MealReviewCard, { useShownReviewChips } from './MealReviewCard'
import StarRatingDisplay from './StarRatingDisplay'

const RATING_COUNT_DISPLAY_CAP = 1000

const MealReviews = ({
  mealID,
  rating,
  shouldScrollToReview,
  triggerRef,
}: {
  mealID: number
  rating: MealRating | undefined
  shouldScrollToReview?: boolean
  triggerRef: RefObject<HTMLButtonElement>
}) => {
  const summaryState = useMealReviewSummary({ mealID })

  if (summaryState.status === 'unavailable' && !rating) {
    return null
  }

  return (
    <DetailsCollapsible
      defaultOpen={shouldScrollToReview}
      onCollapsibleClick={trackDetailsSectionOpened}
      sectionID="reviews"
      title={getTitle(summaryState)}
      triggerRef={triggerRef}
    >
      <MealReviewsContent
        mealID={mealID}
        rating={rating}
        shouldScrollToReview={shouldScrollToReview}
        summaryState={summaryState}
      />
    </DetailsCollapsible>
  )
}

export default MealReviews

const NO_CHIP_IDS: string[] = []

// Mounts only while the reviews section is open, so the shown event fires per open.
const MealReviewsContent = ({
  mealID,
  rating,
  shouldScrollToReview,
  summaryState,
}: {
  mealID: number
  rating: MealRating | undefined
  shouldScrollToReview?: boolean
  summaryState: MealReviewSummaryState
}) => {
  const { isWaitingForChips, shownChips } = useShownReviewChips(
    rating?.chips ?? NO_CHIP_IDS,
  )

  // Joined so the shown event doesn't re-fire when the chip list is rebuilt unchanged.
  const shownChipIDs = shownChips.map(({ id }) => id).join(',')

  const eventProps = useMemo(() => {
    return getMealRatingEventProps({
      mealID,
      rating,
      shownChipIDs,
      summaryState,
    })
  }, [mealID, rating, shownChipIDs, summaryState])

  const hasRatingToShow = !!rating || summaryState.status === 'summarized'
  const isReadyToTrackShown =
    hasRatingToShow && !isWaitingForChips && summaryState.status !== 'loading'

  useEffect(() => {
    if (isReadyToTrackShown) {
      track(events.MEAL_DETAILS_RATING_SHOWN, eventProps)
    }
  }, [eventProps, isReadyToTrackShown])

  return (
    <div className="space-y-6">
      {rating && (
        <MealReviewCard
          eventProps={eventProps}
          rating={rating}
          shouldScrollIntoView={shouldScrollToReview}
        />
      )}

      {summaryState.status === 'summarized' && (
        <ReviewSummary summary={summaryState.summary} />
      )}

      {summaryState.status === 'notEnoughReviews' && <NotEnoughReviews />}
    </div>
  )
}

function getTitle(summaryState: MealReviewSummaryState) {
  if (summaryState.status === 'notEnoughReviews') {
    return 'Reviews (0)'
  }

  if (summaryState.status !== 'summarized') {
    return 'Reviews'
  }

  const { ratingCount } = summaryState.summary

  return ratingCount >= RATING_COUNT_DISPLAY_CAP
    ? `Reviews (${RATING_COUNT_DISPLAY_CAP}+)`
    : `Reviews (${ratingCount})`
}

const ReviewSummary = ({ summary }: { summary: MealReviewSummary }) => {
  return (
    <div>
      <div className="flex items-center gap-x-3">
        <span className="text-k/32_105 font-semibold">
          {summary.averageStars.toFixed(2)}
        </span>

        <div className="flex items-center gap-x-2">
          <StarRatingDisplay stars={summary.averageStars} />

          <span className="text-k/14_120 text-grey-8">
            {summary.ratingCount} ratings
          </span>
        </div>
      </div>

      {summary.topChips.length > 0 && (
        <div className="mt-4">
          <h4 className="text-k/20_110">Top mentions for reviews</h4>

          <ul className="mt-3 flex flex-wrap gap-2">
            {summary.topChips.map(({ count, id, name }) => (
              <li
                key={id}
                className="flex items-center gap-x-[10px] rounded-full bg-grey-3 p-3"
              >
                <span className="text-k/14_120 font-semibold">{name}</span>
                <span className="text-k/14_120 text-grey-9">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const NotEnoughReviews = () => {
  return (
    <div className="space-y-2">
      <p className="text-k/20_110">New this week. No reviews yet.</p>
      <p className="text-k/14_120 text-grey-10">
        Check back in the future to see what others think of this meal.
      </p>
    </div>
  )
}
